import {
  buildAddToCartProduct,
  buildCartLineProduct,
} from '@/lib/elevar/cart-events';
import { buildItemViewProduct } from '@/lib/elevar/item-view';
import { getMoneyAmount } from '@/lib/elevar/shared';

// Ecommerce events for Klaviyo flows (browse and cart abandonment). The
// Klaviyo app embed sends these on a Shopify theme, but Birch is headless, so
// nothing sends them unless we do. Products are built with the Elevar helpers
// so both tools see the same data.
//
// Metric and property names follow Klaviyo's standard Shopify events, so
// existing flows pick them up. Pushed to `_klOnsite` like "Viewed Page"
// (KlaviyoPageView): the onsite script drains the queue once Osano allows it,
// and Klaviyo only records events for identified visitors.

const track = (metric, properties) => {
  if (typeof window === 'undefined') return;
  if (!process.env.NEXT_PUBLIC_KLAVIYO_PUBLIC_API_KEY) return;

  window._klOnsite = window._klOnsite || [];
  window._klOnsite.push(['track', metric, properties]);
};

const toNumber = (amount) => Number(amount) || 0;

// Elevar product (see lib/elevar) → Klaviyo item.
const toKlaviyoItem = (product) => ({
  ProductID: product.product_id,
  SKU: product.id,
  ProductName: product.name,
  VariantName: product.variant,
  VariantID: product.variant_id,
  Quantity: toNumber(product.quantity),
  ItemPrice: toNumber(product.price),
  RowTotal: toNumber(product.price) * toNumber(product.quantity),
  ProductURL: product.url,
  ImageURL: product.image,
  ProductCategories: product.category ? [product.category] : [],
});

/** Sends "Viewed Product" for the PDP's selected variant. */
export function klaviyoViewedProduct(productData, selectedVariant, pricing) {
  const product = buildItemViewProduct(productData, selectedVariant, pricing);
  if (!product) return;

  track('Viewed Product', {
    ProductName: product.name,
    ProductID: product.product_id,
    SKU: product.id,
    VariantName: product.variant,
    VariantID: product.variant_id,
    Categories: product.category ? [product.category] : [],
    ImageURL: product.image,
    URL: product.url,
    Brand: product.brand,
    Price: toNumber(product.price),
    CompareAtPrice: toNumber(product.compare_at_price),
  });
}

/**
 * Sends one "Added to Cart" per added item (Klaviyo's event describes a single
 * item), with the updated cart for the cart abandonment flow.
 */
export function klaviyoAddedToCart(addedItems, cart, pricing) {
  const cartProducts = (cart?.lines || []).map((cartItem, index) =>
    buildCartLineProduct(cartItem, { index, pricing }),
  );
  const cartTotal = toNumber(
    getMoneyAmount(pricing?.cart?.total(cart) ?? cart?.cost?.subtotalAmount),
  );

  (addedItems || []).forEach((cartItem) => {
    const added = buildAddToCartProduct(cartItem, '', pricing);

    track('Added to Cart', {
      $value: cartTotal,
      AddedItemProductName: added.name,
      AddedItemProductID: added.product_id,
      AddedItemSKU: added.id,
      AddedItemVariantName: added.variant,
      AddedItemVariantID: added.variant_id,
      AddedItemCategories: added.category ? [added.category] : [],
      AddedItemImageURL: added.image,
      AddedItemURL: added.url,
      AddedItemPrice: toNumber(added.price),
      AddedItemQuantity: toNumber(added.quantity),
      ItemNames: cartProducts.map((product) => product.name),
      CheckoutURL: cart?.checkoutUrl || '',
      Items: cartProducts.map(toKlaviyoItem),
    });
  });
}
